import { useTranslation } from 'react-i18next';
import { useAuth, type GlobalRole } from '@/features/auth';
import { fullNameOf, useCurrentUserQuery, type Gender } from '@/features/user';
import { Alert, Avatar, Badge, Button, Card, CardHeader, PageHeader, Spinner } from '@/shared/ui';
import type { ReactNode } from 'react';

/** Pojedynczy wiersz listy danych. */
function DataRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-border flex flex-col gap-0.5 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <dt className="text-fg-muted shrink-0 text-xs font-semibold tracking-[0.06em] uppercase sm:w-48">
        {label}
      </dt>
      <dd className="text-fg min-w-0 text-sm">{children}</dd>
    </div>
  );
}

/** Dobiera kolor etykiety statusu konta. */
function statusTone(status: string) {
  if (status === 'ACTIVE') return 'success' as const;
  if (status === 'BLOCKED') return 'danger' as const;
  if (status === 'PENDING') return 'warning' as const;
  return 'neutral' as const;
}

/**
 * Dane konta zalogowanego uzytkownika — wejscie ikona konta w pasku gornym.
 *
 * Prezentuje komplet danych z dwoch zrodel: profil z `GET /api/users/{id}`
 * oraz role globalne i dane sesji z tokena dostepu — backend nie zwraca rol
 * w `UserResponse`.
 */
export function AccountPage() {
  const { t } = useTranslation('common');
  const { user, sessionId, accessTokenExpiresAt } = useAuth();
  const { data: profile, isPending, isError, refetch } = useCurrentUserQuery();

  const genderLabels: Record<Gender, string> = {
    MALE: t('user.genders.MALE'),
    FEMALE: t('user.genders.FEMALE'),
  };

  const roleLabels: Record<GlobalRole, string> = {
    PLAYER: t('roles.PLAYER'),
    TRAINER: t('roles.TRAINER'),
    OWNER: t('roles.OWNER'),
    ADMIN: t('roles.ADMIN'),
  };

  const statusLabels: Record<string, string> = {
    ACTIVE: t('user.statuses.ACTIVE'),
    INACTIVE: t('user.statuses.INACTIVE'),
    BLOCKED: t('user.statuses.BLOCKED'),
    PENDING: t('user.statuses.PENDING'),
  };

  const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <PageHeader
        eyebrow={t('user.accountData')}
        title={profile ? fullNameOf(profile) : t('state.loading')}
        description={profile?.email}
      />

      {isPending && (
        <Card padding="lg" className="flex items-center justify-center gap-3 py-12">
          <Spinner size="lg" label={null} />
          <span className="text-fg-muted text-sm">{t('state.loading')}</span>
        </Card>
      )}

      {isError && (
        <Alert tone="danger" title={t('user.loadFailed')}>
          <div className="flex flex-col items-start gap-3">
            <p>{t('errors.unexpected')}</p>
            <Button size="sm" variant="secondary" onClick={() => void refetch()}>
              {t('actions.retry')}
            </Button>
          </div>
        </Alert>
      )}

      {profile && (
        <>
          <Card
            padding="lg"
            className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left"
          >
            <Avatar name={fullNameOf(profile)} size="xl" />
            <div className="flex min-w-0 flex-col gap-2">
              <div>
                <p className="text-fg-strong text-xl font-bold tracking-[-0.02em]">
                  {fullNameOf(profile)}
                </p>
                <p className="text-fg-muted text-sm">{profile.email}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <Badge tone={statusTone(profile.status)}>
                  {statusLabels[profile.status] ?? profile.status}
                </Badge>
                {user?.roles.map((role) => (
                  <Badge key={role} tone="brand">
                    {roleLabels[role]}
                  </Badge>
                ))}
              </div>
            </div>
          </Card>

          <Card padding="lg">
            <CardHeader title={t('user.accountData')} />
            <dl className="flex flex-col">
              <DataRow label={t('user.id')}>
                <span className="font-mono text-[0.8125rem] break-all">{profile.id}</span>
              </DataRow>
              <DataRow label={t('user.firstName')}>{profile.firstName}</DataRow>
              <DataRow label={t('user.lastName')}>{profile.lastName}</DataRow>
              <DataRow label={t('user.email')}>{profile.email}</DataRow>
              <DataRow label={t('user.gender')}>
                {genderLabels[profile.gender] ?? profile.gender}
              </DataRow>
              <DataRow label={t('user.status')}>
                <Badge tone={statusTone(profile.status)} size="sm">
                  {statusLabels[profile.status] ?? profile.status}
                </Badge>
              </DataRow>
              <DataRow label={t('user.roles')}>
                {user?.roles.length ? (
                  <span className="flex flex-wrap gap-1.5">
                    {user.roles.map((role) => (
                      <Badge key={role} tone="brand" size="sm">
                        {roleLabels[role]} <span className="opacity-60">({role})</span>
                      </Badge>
                    ))}
                  </span>
                ) : (
                  <span className="text-fg-subtle">{t('user.noRoles')}</span>
                )}
              </DataRow>
            </dl>
          </Card>

          <Card padding="lg">
            <CardHeader title={t('session.title')} />
            <dl className="flex flex-col">
              <DataRow label={t('session.id')}>
                <span className="font-mono text-[0.8125rem] break-all">{sessionId ?? '—'}</span>
              </DataRow>
              <DataRow label={t('session.tokenExpiresAt')}>
                {accessTokenExpiresAt ? dateFormatter.format(accessTokenExpiresAt) : '—'}
              </DataRow>
            </dl>
            <p className="text-fg-subtle mt-3 text-xs">{t('session.description')}</p>
          </Card>
        </>
      )}
    </div>
  );
}
